// Chamadas à API (services/api) em /api, com o token da sessão. Erros chegam em português.
import type {
  Colecao, Conquistas, DivisaoNaTela, PainelPasse, EscopoRanking, HistoricoTrio, Liga, NivelRodagem, OfensivaNaTela, PainelMissoes, PainelTrios, Periodo, QuenteAgora,
  PoteDaPartida, RankingNaTela, RecompensaPaga, TabelaDeRecompensas, TipoRanking, Trio,
} from '@truco6/gamificacao';
import type { ChavePacote, Ciclo, Plano, PlanoPago } from '@truco6/planos';
import type { Sessao } from './conta.ts';

export type Metodo = 'cartao' | 'pix';
export type StatusCobranca = 'aberta' | 'paga' | 'vencida' | 'anulada' | 'reembolsada';

export interface ResumoAssinatura {
  id: string;
  plano: PlanoPago;
  ciclo: Ciclo;
  metodo: Metodo;
  status: 'ativa' | 'aguardando_pix' | 'cancelada';
  cancelaNoFim: boolean;
  fimDoPeriodo: number | null;
  faturaAberta: string | null;
  podeDesistirAte: number | null;
  valorPago: number; // centavos: o que a desistência devolve
  cortesia: boolean; // desconto de 100% por código promocional
  cortesiaAte: number | null; // fim do desconto
  semCartao: boolean; // sem cartão salvo: a renovação depois da cortesia não cobra
}

export interface CobrancaEu {
  id: string;
  plano: PlanoPago;
  ciclo: Ciclo;
  metodo: Metodo;
  valor: number; // centavos
  status: StatusCobranca;
  data: number;
  venceEm: number | null;
  url: string | null; // só de fatura a pagar
}

export interface SituacaoLimite {
  plano: Plano;
  limite: number | null;
  usadas: number;
  bonus: number;
  restantes: number | null;
  voltaEm: number;
}

export interface Indicado { apelido: string; avatar: string | null }

export interface DadosIndicacao {
  codigo: string;
  link: string;
  entraram: Indicado[];
  bonusGanho: number;
}

export interface Eu {
  plano: Plano;
  cobranca: 'ligada' | 'desligada';
  assinatura: ResumoAssinatura | null;
  cobrancas: CobrancaEu[];
  limite: SituacaoLimite;
  indicacao: DadosIndicacao | null; // convidado não tem
  tentos: SituacaoTentos;
}

// ---------- Tentos ----------
export interface SituacaoTentos {
  saldo: number;
  reservado: number;
  disponivel: number;
  bloqueado: boolean;
}

export type TipoMovimento =
  | 'credito_plano' | 'compra' | 'compra_reembolsada' | 'ingresso_debitado' | 'ingresso_devolvido'
  | 'premio' | 'credito_admin' | 'debito_admin' | 'cosmetico' | 'contestacao_revertida' | 'recompensa';

export interface ItemExtrato {
  id: number;
  tipo: TipoMovimento;
  quantidade: number;
  em: number;
  detalhes: Record<string, unknown>;
  sessao?: string; // compra: a sessão do checkout, para desistir
}

export interface PacoteNaLoja {
  chave: ChavePacote;
  tentos: number;
  centavos: number;
  desconto: number;
  liberado: boolean; // false: passaria do teto do mês
}

export interface PainelTentos extends SituacaoTentos {
  reservas: { sala: string; quantidade: number; criadaEm: number }[];
  extrato: ItemExtrato[];
  mais: boolean;
  loja: {
    ligada: boolean;
    pix: boolean; // false: o checkout abre só com cartão
    teto: number;
    gastoNoMes: number;
    pacotes: PacoteNaLoja[];
    pendentes: { sessao: string; tentos: number }[]; // Pix gerado esperando pagamento
    desistiveis: Record<string, number>; // sessão -> até quando dá para desistir
  };
}

export type StatusCompra = 'aberta' | 'falhou' | 'paga' | 'reembolsada' | 'contestada';

export const lerTentos = (s: Sessao, antes?: number) => api<PainelTentos>(s, 'GET', '/tentos' + (antes ? `?antes=${antes}` : ''));

// ---------- Liga da Semana ----------
export type { DivisaoNaTela };

export interface PainelLiga {
  liga: Liga; // sem patrocinador: "Liga da Semana Truco de 6", marca e prêmio null
  temporada: string;
  divisao: DivisaoNaTela;
  pontos: { semana: number; mes: number; temporada: number }; // pontos do ranking, de todas as partidas; na virada da temporada, zeram
  rodagem: NivelRodagem; // nível de Rodagem
  ofensiva: OfensivaNaTela; // dias seguidos, descansos e o calendário da semana
  quente: QuenteAgora; // a Rodada quente da próxima partida, se começar agora
  missoes: PainelMissoes; // missões do dia e da semana e o baú
  limite: SituacaoLimite;
}

export const lerLiga = (s: Sessao) => api<PainelLiga>(s, 'GET', '/liga');

export const lerRanking = (s: Sessao, tipo: TipoRanking, escopo: EscopoRanking, periodo: Periodo) =>
  api<RankingNaTela>(s, 'GET', `/ranking?${new URLSearchParams({ tipo, escopo, periodo })}`);
// Rei do Pote no fim da partida valendo Tentos: o saldo da partida e a posição da semana, agora e antes dela.
export const lerPoteDaPartida = (s: Sessao, codigo: string, partida: number) =>
  api<PoteDaPartida>(s, 'GET', `/ranking/pote/partida?${new URLSearchParams({ codigo, partida: String(partida) })}`);
export const lerConquistas = (s: Sessao) => api<Conquistas & { recompensas?: TabelaDeRecompensas }>(s, 'GET', '/conquistas');
export const lerMissoes = (s: Sessao) => api<PainelMissoes>(s, 'GET', '/missoes');
export const lerColecao = (s: Sessao) => api<Colecao>(s, 'GET', '/colecao');
export const lerPasse = (s: Sessao) => api<PainelPasse>(s, 'GET', '/passe');
export const equiparCosmetico = (s: Sessao, id: string) => api<Colecao>(s, 'POST', '/colecao/equipar', { id });
export const comprarCosmetico = (s: Sessao, id: string) => api<Colecao>(s, 'POST', '/colecao/comprar', { id });
export const trocarMissao = (s: Sessao, slot: number) => api<PainelMissoes>(s, 'POST', '/missoes/trocar', { slot });
export const abrirBau = (s: Sessao, tipo: 'dia' | 'retorno') => api<{ paga: RecompensaPaga | null; painel: PainelMissoes }>(s, 'POST', '/bau/abrir', { tipo });
// Rosto e cidade no perfil rendem Rodagem, uma vez. A API confere no banco; o rosto e a cidade vão junto só para o
// desenvolvimento local. Falha aqui não atrapalha salvar o perfil.
export function avisarPerfilCompleto(s: Sessao, p: { avatar: string | null; cidadeIbge: number | null }) {
  if (!p.avatar || p.cidadeIbge == null) return;
  api(s, 'POST', '/perfil/completo', { avatar: p.avatar, cidadeIbge: p.cidadeIbge }).catch(() => {});
}

// ---------- Trio fixo ----------
// O apelido e o avatar vão junto só para o desenvolvimento local; na produção, a API lê do perfil.
type PerfilCurto = { apelido: string; avatar: string | null };
export type SituacaoConvite = 'pode' | 'ja_esta' | 'cheio' | 'limite';
export interface TrioAberto { trio: Omit<Trio, 'convite'> & { convite: string | null }; membro: boolean; ativo: boolean; historico: HistoricoTrio | null }
export const lerTrios = (s: Sessao) => api<PainelTrios>(s, 'GET', '/trios');
export const lerTrio = (s: Sessao, id: string) => api<TrioAberto>(s, 'GET', `/trios/${encodeURIComponent(id)}`);
export const criarTrio = (s: Sessao, nome: string, escudo: string, perfil: PerfilCurto) => api<Trio>(s, 'POST', '/trios', { nome, escudo, perfil });
export const sairDoTrio = (s: Sessao, id: string) => api<{ ok: true }>(s, 'POST', `/trios/${encodeURIComponent(id)}/sair`);
export const lerConviteDeTrio = (s: Sessao, codigo: string) =>
  api<{ trio: Trio; situacao: SituacaoConvite; limite: number; mensagem: string | null }>(s, 'GET', `/trios/convite/${codigo}`);
export const entrarNoTrio = (s: Sessao, codigo: string, perfil: PerfilCurto) => api<Trio>(s, 'POST', `/trios/convite/${codigo}/entrar`, { perfil });

export class ErroDaApi extends Error {
  readonly codigo: string;
  readonly status: number;
  constructor(status: number, codigo: string, mensagem: string) {
    super(mensagem);
    this.status = status;
    this.codigo = codigo;
  }
}

export async function api<T>(s: Sessao, metodo: 'GET' | 'POST' | 'PUT' | 'DELETE', caminho: string, corpo?: unknown): Promise<T> {
  let r: Response;
  try {
    const headers: Record<string, string> = { authorization: 'Bearer ' + (await s.token()) };
    const comCorpo = metodo === 'POST' || metodo === 'PUT';
    if (comCorpo) headers['content-type'] = 'application/json';
    r = await fetch('/api' + caminho, { method: metodo, headers, body: comCorpo ? JSON.stringify(corpo ?? {}) : undefined });
  } catch {
    throw new ErroDaApi(0, 'sem_conexao', 'Sem conexão com o servidor. Tente de novo.');
  }
  let dados: unknown = null;
  try { dados = await r.json(); } catch { /* resposta sem JSON: a API está fora do ar */ }
  if (r.ok && dados) return dados as T;
  const erro = (dados as { erro?: { codigo?: string; mensagem?: string } } | null)?.erro;
  if (erro?.mensagem) throw new ErroDaApi(r.status, erro.codigo ?? 'erro', erro.mensagem);
  throw new ErroDaApi(r.status, 'fora_do_ar', 'O servidor está fora do ar agora. Tente mais tarde.');
}

export const lerEu = (s: Sessao) => api<Eu>(s, 'GET', '/eu');

// ---------- datas e valores, sempre na hora de São Paulo ----------
const FUSO = 'America/Sao_Paulo';

export function diaMes(ms: number): string {
  return new Date(ms).toLocaleDateString('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit' });
}

export function dataCompleta(ms: number): string {
  return new Date(ms).toLocaleDateString('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit', year: 'numeric' });
}

// A mesma data daqui a um ano (renovação do anual).
export function daquiAUmAno(ms: number): number {
  const d = new Date(ms);
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return d.getTime();
}

// Data da renovação: dia e mês no mensal, com o ano no anual.
export function dataDoCiclo(ms: number, ciclo: Ciclo): string {
  return ciclo === 'anual' ? dataCompleta(ms) : diaMes(ms);
}

export function diaDoMes(ms: number): number {
  return Number(new Date(ms).toLocaleDateString('pt-BR', { timeZone: FUSO, day: 'numeric' }));
}

export function reais(centavos: number): string {
  return 'R$ ' + (centavos / 100).toFixed(2).replace('.', ',');
}

export const NOME_DO_METODO: Record<Metodo, string> = { cartao: 'Cartão', pix: 'Pix' };
